/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { RadioGroup } from "@radix-ui/react-radio-group";
import { useEffect, useState } from "react";

import { spacing } from "../../../../../global/emotion";
import { RadioInput } from "./RadioInput.emotion";

const listStyle: Interpolation = {
  display: "flex",
  flexDirection: "column",
  flexWrap: "nowrap",
  gap: spacing.ms,
};

type RadioListProps<T extends string = string> = {
  name: string;
  options: { label: string; value: T }[] | T[];
  defaultValue?: T;
  value?: T;
  onChange?: (newVal: T) => void;
};

export const RadioList = <T extends string = string>({
  options,
  defaultValue,
  name,
  value,
  onChange,
}: RadioListProps<T>) => {
  const [selected, setSelected] = useState<T | null>(
    defaultValue ?? value ?? null,
  );

  useEffect(() => {
    if (selected !== value) setSelected(value);
  }, [value]);

  useEffect(() => {
    if (selected !== value) onChange(selected);
  }, [selected]);

  return (
    <RadioGroup
      asChild
      name={name}
      value={selected}
      defaultValue={defaultValue}
      onValueChange={(newVal) => setSelected(newVal as T)}
    >
      <div css={listStyle}>
        {options.map((option) => {
          let value: T, label: string;
          if (typeof option === "string") {
            value = option as T;
            label = option;
          } else {
            value = option.value;
            label = option.label;
          }
          return (
            <RadioInput<T>
              key={value}
              value={value}
              label={label}
              name={name}
              isSelected={selected === value}
              onChange={(checked) => checked && setSelected(value)}
            />
          );
        })}
      </div>
    </RadioGroup>
  );
};
